import React, { useEffect, useRef, useState, memo } from 'react'
import {
  MapPropsType,
  CollectionMarkerItemType,
  UniteMarkerModuleItem,
} from './types'
import usePrevious from '../../hooks/usePrevious'

// Components
import AdminMarker from '../AdminMarker'

// Style
import './styles.scss'

function areEqual(prevProps: any, nextProps: any) {
  return prevProps.markers === nextProps.markers
}

export default memo(
  ({
    image,
    title,
    style,
    markers,
    modules,
    themes,
    onReplaceMarker,
    onRemoveMarker,
  }: MapPropsType) => {
    if (!image) return <></>

    const [collectionMarkers, setCollectionMarkers] = useState<
      CollectionMarkerItemType[]
    >([])

    const prevMarkers = usePrevious(markers)

    useEffect(() => {
      if (prevMarkers !== markers) {
        const flagTypes = ['start', 'finish']

        const uniteMarkerModules: UniteMarkerModuleItem[] = []

        markers.forEach(marker => {
          if (flagTypes.includes(marker.type)) {
            uniteMarkerModules.push(marker)
          } else {
            modules.forEach(module => {
              const markerModuleLesson = module.module_lessons.find(
                moduleLesson =>
                  moduleLesson.module_lesson_id === marker.module_lesson_id
              )
              if (markerModuleLesson) {
                uniteMarkerModules.push({
                  ...marker,
                  lessonTitle: markerModuleLesson.lesson.title,
                  moduleTitle: markerModuleLesson.module.title,
                  moduleLesson: markerModuleLesson,
                })
              }
            })
          }
        })

        const continueMarkers: string[] = []

        uniteMarkerModules
          .filter(marker => !flagTypes.includes(marker.type))
          .forEach(marker => {
            if (
              marker.moduleLesson?.is_joined &&
              !marker.moduleLesson?.is_finished
            ) {
              continueMarkers.push(marker.marker_id)
            }
          })

        if (!continueMarkers.length) {
          uniteMarkerModules
            .filter(marker => marker.moduleLesson)
            .sort((a, b) => {
              if (a.moduleLesson && b.moduleLesson) {
                return a.moduleLesson.index_number - b.moduleLesson.index_number
              } else {
                return 0
              }
            })
            .forEach(marker => {
              if (marker.moduleLesson?.is_finished) {
                continueMarkers.push(marker.marker_id)
              }
            })
        }

        const modulesMarkers: CollectionMarkerItemType[] =
          uniteMarkerModules.map(marker => {
            const moduleLesson = marker.moduleLesson
            const lessonItem = moduleLesson?.lesson

            const color = themes
              ? themes.find(theme => theme.theme_id === lessonItem?.theme_id)
                  ?.color
              : '#A3A3A3'

            return {
              ...marker,
              color,
              indexNumber: moduleLesson?.index_number,
            }
          })
        setCollectionMarkers([...modulesMarkers])
      }
    }, [markers])

    const DragPlace = ({ children }: { children: any }): JSX.Element => {
      const refMarkerId = useRef<string>('')
      const refDrag = useRef<HTMLDivElement | null>(null)

      const refDistX = useRef<number>(0)
      const refDistY = useRef<number>(0)

      const refOffsetX = useRef<number>(0)
      const refOffsetY = useRef<number>(0)

      const refIsNew = useRef<boolean>(false)

      const onStart = (
        id: string,
        element: { current: HTMLDivElement },
        clientX: number,
        clientY: number,
        offsetY?: number,
        offsetX?: number
      ) => {
        refMarkerId.current = id
        refDrag.current = element.current

        refOffsetX.current = offsetX || 0
        refOffsetY.current = offsetY || 0

        refIsNew.current =
          refDrag.current.offsetLeft < 0 || refDrag.current.offsetTop < 0

        if (refDrag.current) {
          refDistX.current = Math.abs(refDrag.current.offsetLeft - clientX)
          refDistY.current = Math.abs(refDrag.current.offsetTop - clientY)
          refDrag.current.style.pointerEvents = 'none'
          refDrag.current.style.zIndex = '10'
        }
      }

      const onEnd = () => {
        if (refDrag.current) {
          const { top, left } = refDrag.current.style
          refDrag.current.style.pointerEvents = 'initial'
          refDrag.current.style.zIndex = '2'

          const positionX = parseInt(left) + refOffsetX.current
          const positionY = parseInt(top) + refOffsetY.current

          const newCollectionMarkers = collectionMarkers.map(marker => {
            if (marker.marker_id === refMarkerId.current) {
              return {
                ...marker,
                position_x: positionX,
                position_y: positionY,
              }
            } else {
              return marker
            }
          })

          setCollectionMarkers([...newCollectionMarkers])

          onReplaceMarker(refMarkerId.current, positionX, positionY)
        }
      }

      const onMove = (event: any) => {
        if (refDrag.current) {
          if (refDrag.current.style.pointerEvents === 'none') {
            refDrag.current.style.left = `${event.clientX - refDistX.current}px`
            refDrag.current.style.top = `${event.clientY - refDistY.current}px`
            // const { offsetTop, offsetLeft } = refDrag.current
            //
            // const mapHeight = refImageMap.current.clientHeight
            // const mapWidth = refImageMap.current.clientWidth
            //
            // if (
            //   (offsetTop < 0 ||
            //     offsetLeft < 0 ||
            //     offsetTop > mapHeight - 30 ||
            //     offsetLeft > mapWidth - 30) &&
            //   !refIsNew.current
            // ) {
            //   refDrag.current.style.top =
            //     offsetTop < 0 ? '0' : offsetTop.toString()
            //   refDrag.current.style.left =
            //     offsetLeft < 0 ? '0' : offsetLeft.toString()
            //   onEnd()
            // }
          }
        }
      }

      return (
        <div
          onMouseMove={onMove}
          onTouchMove={onMove}
          onMouseUp={onEnd}
          onTouchEnd={onEnd}
          role="presentation"
          style={{
            width: '100%',
            position: 'absolute',
            top: 0,
            bottom: 0,
          }}
        >
          {children(onStart)}
        </div>
      )
    }

    return (
      <div className={'admin-map'} style={style}>
        <div className={'admin-map__background'} />
        <h1>{title}</h1>
        <div className={'admin-map__outer'}>
          <img src={image} />
          <DragPlace>
            {(
              onStart: (
                id: string,
                ref: { current: HTMLDivElement | null },
                clientX: number,
                clientY: number,
                offsetY?: number,
                offsetX?: number
              ) => void
            ) =>
              collectionMarkers.map(marker => (
                <AdminMarker
                  id={marker.marker_id}
                  key={marker.marker_id}
                  type={marker.type}
                  color={marker?.color}
                  y={marker.position_y}
                  x={marker.position_x}
                  indexNumber={marker.indexNumber}
                  lessonTitle={marker.lessonTitle || ''}
                  moduleTitle={marker.moduleTitle || ''}
                  onStart={onStart}
                  onRemoveMarker={onRemoveMarker}
                />
              ))
            }
          </DragPlace>
        </div>
      </div>
    )
  },
  areEqual
)
